import { useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { useAcsEvents } from "./useAcsEvents";
import { ACS_EVENTS_REFRESH_INTERVAL_SECONDS } from "./acsEventsConfig";
import "./AcsEvents.css";

function formatEventTime(value: string) {
  // Preserve the date/time recorded by ACS, even if it contains an offset.
  const time = moment.parseZone(value, moment.ISO_8601, true);
  return time.isValid() ? time.format("DD MMM YYYY, HH:mm:ss") : value;
}

export const AcsEvents = () => {
  const log = useAcsEvents();
  const busy = log.pending !== null;

  useLayoutEffect(() => {
    const root = document.getElementById("root");
    root?.classList.add("acs-events-layout");
    return () => root?.classList.remove("acs-events-layout");
  }, []);

  return (
    <main className="main acs-events">
      <div className="wrap">
        <div className="intro">
          <h1 className="intro__title">Token <span className="intro__title-highlight">events</span></h1>
          <Link to="/token" className="btn">Manage tokens</Link>
        </div>

        <section className="manager" aria-label="Access control events">
          <div className="acs-events__toolbar">
            <div className="acs-events__status" role="status">
              <span className={`acs-events__indicator${log.paused || log.accessDenied || log.error ? " acs-events__indicator--paused" : ""}`} aria-hidden="true" />
              <span>{log.accessDenied ? "Updates stopped" : log.paused ? "Updates paused"
                : `Auto-refresh every ${ACS_EVENTS_REFRESH_INTERVAL_SECONDS} ${ACS_EVENTS_REFRESH_INTERVAL_SECONDS === 1 ? "second" : "seconds"}`}</span>
              {log.lastChecked && <span className="acs-events__checked">Last checked {log.lastChecked.toLocaleTimeString()}</span>}
            </div>
            <div className="acs-events__actions">
              <button type="button" className="btn btn--transparent" onClick={log.togglePaused} disabled={log.accessDenied}>
                {log.paused ? "Resume updates" : "Pause updates"}
              </button>
              <button type="button" className="btn" onClick={log.refresh} disabled={busy || log.accessDenied}>
                {log.pending === "refresh" ? "Refreshing…" : "Refresh now"}
              </button>
            </div>
          </div>

          {log.error && (
            <div className="acs-events__error" role="alert">
              <span>{log.error.message}</span>
              {log.accessDenied ? <Link to="/login" className="link">Sign in</Link> : (
                <button type="button" className="link" onClick={log.retry} disabled={busy}>Retry</button>
              )}
            </div>
          )}

          {!log.loaded && !log.error && <p className="acs-events__empty" role="status">Loading events…</p>}
          {log.loaded && log.events.length === 0 && !log.accessDenied && (
            <div className="acs-events__empty">
              <h2>No events yet</h2>
              <p>{log.paused ? "Resume updates to check for new scans." : "New scans will appear here automatically."}</p>
            </div>
          )}

          {log.events.length > 0 && (
            <div className="manager__content acs-events__table-container" tabIndex={0} role="region" aria-label="ACS event history">
              <table className="table acs-events__table">
                <caption className="sr-only">Token events, ordered from newest to oldest</caption>
                <thead>
                  <tr className="table__row">
                    {["Scan time", "Token number", "Token name", "Owner", "Event type", "Door", "Control"].map((title) => (
                      <th className="table__head" scope="col" key={title}>{title}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {log.events.map((event) => (
                    <tr className="table__row" key={event.id}>
                      <td className="table__cell acs-events__time"><time dateTime={event.eventTime}>{formatEventTime(event.eventTime)}</time></td>
                      <td className="table__cell acs-events__token">{event.cardNo || "—"}</td>
                      <td className="table__cell">{event.tokenName || "—"}</td>
                      <td className="table__cell">{event.ownerName || <span className="acs-events__muted">Unassigned</span>}</td>
                      <td className="table__cell">{event.eventType || <span className="acs-events__muted">Unknown</span>}</td>
                      <td className="table__cell">{event.doorName || "—"}</td>
                      <td className="table__cell">{event.controlName || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {log.loaded && !log.accessDenied && (
            <div className="acs-events__footer">
              <span>{log.events.length} {log.events.length === 1 ? "event" : "events"} loaded</span>
              {log.hasMoreOlder ? (
                <button type="button" className="btn btn--transparent" onClick={log.loadOlder} disabled={busy}>
                  {log.pending === "older" ? "Loading older events…" : "Load older"}
                </button>
              ) : log.events.length > 0 ? <span>No older events</span> : null}
            </div>
          )}
        </section>
      </div>
    </main>
  );
};
